import instance, { ADMIN_PREFIX } from 'services/api';
import {
  OrganizationDetail,
  OrganizationInput,
  OrganizationListRow
} from 'types/api/organizations';

/**
 * GET /api/v2/organizations — PaginatedResponse envelope, full org rows.
 * Used by the Organizations page (table + edit modal). Service flattens
 * `data` and requests limit=999.
 */
export const fetchOrganizations = async (): Promise<OrganizationListRow[]> => {
  const res = await instance.get<{ data: OrganizationListRow[] }>(
    `${ADMIN_PREFIX}/organizations`,
    { params: { page: 1, limit: 999 } }
  );

  return res.data.data ?? [];
};

/**
 * GET /api/v2/organizations/select — slim { id, name, image } projection
 * as a bare array. Used by the layout header org-picker so the dropdown
 * load doesn't pull settings / commission / additional_info bytes it
 * never renders.
 */
export const fetchOrganizationsForPicker = async (): Promise<
  Array<Pick<OrganizationListRow, 'id' | 'name' | 'image'>>
> => {
  const res = await instance.get<
    Array<Pick<OrganizationListRow, 'id' | 'name' | 'image'>>
  >(`${ADMIN_PREFIX}/organizations/select`);

  return res.data ?? [];
};

export const fetchOrganizationById = async (
  id: string | number
): Promise<OrganizationDetail> => {
  const res = await instance.get<OrganizationDetail>(
    `${ADMIN_PREFIX}/organizations/${id}`
  );

  return res.data;
};

export const createOrganization = (input: OrganizationInput) =>
  instance.post<OrganizationDetail>(`${ADMIN_PREFIX}/organizations`, input);

export const updateOrganization = (
  id: string | number,
  input: OrganizationInput
) =>
  instance.put<OrganizationDetail>(
    `${ADMIN_PREFIX}/organizations/${id}`,
    input
  );

export const deleteOrganization = (id: string | number) =>
  instance.delete<{ status: boolean }>(`${ADMIN_PREFIX}/organizations/${id}`);

/**
 * GET /api/v2/organizations/:id/children — slim sub-org picker
 * projection (id, name, image). Bare array. Mirrors /organizations/select
 * for the child level. Replaces the previous pattern of fetching the
 * heavy /organizations/:id detail just to read .children.
 */
export const fetchSubOrganizations = async (
  parentId: string | number
): Promise<Array<Pick<OrganizationListRow, 'id' | 'name' | 'image'>>> => {
  const res = await instance.get<
    Array<Pick<OrganizationListRow, 'id' | 'name' | 'image'>>
  >(`${ADMIN_PREFIX}/organizations/${parentId}/children`);

  return res.data ?? [];
};
